import { Link, useSearchParams } from 'react-router';
import type { MonitoringCheck, Service } from '@bekebi/contracts/api';
import { Button, buttonClass } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { isServiceId, useServices } from '../services/api';
import { CHECK_KIND_TITLES, formatDateTime } from '../services/format';
import { useChecks } from './api';
import { MonitoringHeader } from './monitoring-header';
import { PortalServerSection } from './portal-server';
import { CheckStateBadge, STALE_EXPLANATION, formatInterval, targetSummary } from './present';

/**
 * Monitoring overview (/app/monitoring): the configured checks from GET /api/monitoring/checks,
 * page by page, with each check's service, state (as stored by the worker, including the API's
 * `stale` flag), last check time and interval. The service filter is the API's own `serviceId`
 * filter; the enabled/disabled filter applies to the checks already loaded. This is the Checks tab;
 * Alerts is its sibling (/app/monitoring/alerts).
 */
type StateFilter = 'all' | 'enabled' | 'disabled';

export function MonitoringOverview() {
  const [params, setParams] = useSearchParams();
  const serviceFilter = isServiceId(params.get('service') ?? undefined)
    ? params.get('service')
    : null;
  const stateFilter: StateFilter =
    params.get('state') === 'enabled' || params.get('state') === 'disabled'
      ? (params.get('state') as StateFilter)
      : 'all';
  const checks = useChecks(serviceFilter);
  const services = useServices();
  const canManage = useHasPermission('monitoring.manage');

  const setFilter = (key: 'service' | 'state', value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const serviceById = new Map((services.data ?? []).map((s) => [s.id, s]));
  const all = checks.data?.pages.flatMap((p) => p.items) ?? [];
  const shown = all.filter(
    (c) => stateFilter === 'all' || (stateFilter === 'enabled' ? c.enabled : !c.enabled),
  );

  const addLink = canManage ? (
    <Link
      to={serviceFilter ? `/app/monitoring/new?service=${serviceFilter}` : '/app/monitoring/new'}
      className={buttonClass('primary')}
    >
      Add check
    </Link>
  ) : null;

  return (
    <div className="max-w-6xl">
      <MonitoringHeader current="checks" action={addLink}>
        Scheduled service checks, run by the portal&rsquo;s monitoring worker on the server. Results
        are shown as recorded; the browser never contacts the checked services itself. A check with
        no result, or a stale one, means no current result has arrived from the worker.
      </MonitoringHeader>

      <div className="mt-6">
        <PortalServerSection />
      </div>

      <div className="mt-6 flex flex-wrap gap-4" role="group" aria-label="Filters">
        <div>
          <label htmlFor="filter-service" className="block text-xs font-medium text-muted">
            Service
          </label>
          <select
            id="filter-service"
            value={serviceFilter ?? ''}
            onChange={(e) => setFilter('service', e.target.value)}
            className="mt-1 min-h-10 rounded-md border border-line-strong bg-surface px-3 text-sm"
          >
            <option value="">All services</option>
            {(services.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {serviceLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="filter-state" className="block text-xs font-medium text-muted">
            Show
          </label>
          <select
            id="filter-state"
            value={stateFilter}
            onChange={(e) => setFilter('state', e.target.value)}
            className="mt-1 min-h-10 rounded-md border border-line-strong bg-surface px-3 text-sm"
          >
            <option value="all">Enabled and disabled</option>
            <option value="enabled">Enabled only</option>
            <option value="disabled">Disabled only</option>
          </select>
        </div>
      </div>

      <div className="mt-6">
        {checks.isPending ? (
          <LoadingState label="Loading checks…" />
        ) : checks.isError ? (
          <ErrorState
            title="Checks could not be loaded"
            message={checks.error.message}
            onRetry={() => void checks.refetch()}
          />
        ) : all.length === 0 ? (
          <EmptyState
            title="No monitoring checks yet."
            description={
              serviceFilter
                ? 'No checks are configured for this service.'
                : 'No checks are configured. Services stay “Unknown” until they have checks with current results.'
            }
            action={addLink}
          />
        ) : shown.length === 0 ? (
          <EmptyState title="No checks match these filters." />
        ) : (
          <>
            <div
              aria-hidden="true"
              className="hidden grid-cols-[2fr_1.3fr_1.2fr_1.2fr_0.8fr] gap-4 px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted lg:grid"
            >
              <span>Check</span>
              <span>Service</span>
              <span>State</span>
              <span>Last checked</span>
              <span>Interval</span>
            </div>
            <ul aria-label="Checks" className="space-y-2">
              {shown.map((check) => (
                <li key={check.id}>
                  <CheckRow check={check} service={serviceById.get(check.serviceId)} />
                </li>
              ))}
            </ul>
            {shown.some((c) => c.enabled && c.lastStatus !== null && c.stale) ? (
              <p className="mt-3 text-xs text-muted">{STALE_EXPLANATION}</p>
            ) : null}
            {checks.hasNextPage ? (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  onClick={() => void checks.fetchNextPage()}
                  disabled={checks.isFetchingNextPage}
                >
                  {checks.isFetchingNextPage ? 'Loading…' : 'Show more checks'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function serviceLabel(service: Service): string {
  return service.type === 'website' && service.accessUrl
    ? `${service.name} (${service.accessUrl})`
    : service.name;
}

function CheckRow({ check, service }: { check: MonitoringCheck; service: Service | undefined }) {
  return (
    <article className="grid gap-2 rounded-lg border border-line bg-surface p-4 text-sm lg:grid-cols-[2fr_1.3fr_1.2fr_1.2fr_0.8fr] lg:items-center lg:gap-4">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold">
          <Link
            to={`/app/monitoring/${check.id}`}
            className="text-fg underline-offset-4 hover:underline"
          >
            {CHECK_KIND_TITLES[check.kind]}
          </Link>
        </h2>
        <p className="break-all font-mono text-xs text-muted">{targetSummary(check)}</p>
      </div>
      <div>
        <span className="text-muted lg:hidden">Service: </span>
        {service ? (
          <Link
            to={`/app/services/${service.id}`}
            className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            {service.name}
          </Link>
        ) : (
          <span className="text-muted">Service not loaded</span>
        )}
      </div>
      <div>
        <span className="sr-only">State: </span>
        <CheckStateBadge check={check} />
      </div>
      <div className="text-fg-2">
        <span className="text-muted lg:hidden">Last checked: </span>
        {check.lastCheckedAt ? formatDateTime(check.lastCheckedAt) : 'No result yet'}
      </div>
      <div className="text-fg-2">{formatInterval(check.intervalSeconds)}</div>
    </article>
  );
}
